import { useMemo } from 'react'

import { pctToView, VIEW_H, VIEW_W } from '@/geo/types'
import { isStageBody, useEditorStore } from '@/stores/editorStore'

import { AnchorMarker } from './shapes/AnchorMarker'
import { PathShape } from './shapes/PathShape'
import { PortalMarker } from './shapes/PortalMarker'
import { RegionShape } from './shapes/RegionShape'
import { reviewStateOf } from './applyProposal'
import { kindColour, useVocab } from './vocab'

/**
 * The suggestions drawn over (or beside, or under a swipe divider) the current document (§9.4).
 *
 * Only what is still to decide is drawn: a suggestion already taken into the picture, one the
 * reviewer dropped, and one that matches what is there are not. Shapes, spots, ways in and out and
 * paths all get a ghost; the row the review bar is pointing at is highlighted.
 *
 * Three views, all built from the same ghost shapes:
 *  - **overlay** — dashed outlines at 10 % fill with a connector line between every changed pair,
 *    so "it moved the pond" is one glance;
 *  - **swipe** — the same ghosts inside a `<clipPath>` the operator drags;
 *  - **split** — rendered by the layout, which mounts two canvases sharing one `viewport`; here it
 *    behaves like `overlay` for the right pane.
 *
 * Nothing in this layer is interactive: accepting and dropping happen in `ProposalReviewBar`,
 * where the rows have real labels.
 */

export interface ProposalLayerProps {
  /** Swipe divider position, percent of width. Only used by the swipe view. */
  swipeX?: number
}

const NONE: readonly string[] = []

export function ProposalLayer({ swipeX = 50 }: ProposalLayerProps) {
  const vocab = useVocab()
  const doc = useEditorStore((s) => s.doc)
  const proposal = useEditorStore((s) => s.proposal)
  const dropped = useEditorStore((s) => (s.proposal ? (s.proposalDropped[String(s.proposal.id)] ?? NONE) : NONE))
  const accepted = useEditorStore((s) => (s.proposal ? (s.proposalAccepted[String(s.proposal.id)] ?? NONE) : NONE))

  const review = useMemo(() => reviewStateOf(doc, proposal, dropped, accepted), [doc, proposal, dropped, accepted])

  if (!proposal || proposal.view === 'off' || !isStageBody(proposal.body)) return null
  const body = proposal.body
  const clip = proposal.view === 'swipe' ? 'url(#proposal-swipe)' : undefined
  const open = new Set(review.items.map((i) => i.id))
  const focus = proposal.focus ?? null
  const anchorAt = (id: string | undefined) =>
    id ? (body.anchors.find((a) => a.id === id)?.at ?? (isStageBody(doc) ? doc.anchors.find((a) => a.id === id)?.at : undefined)) : undefined

  return (
    <g className="proposal-layer" pointerEvents="none" data-testid="proposal-layer">
      {proposal.view === 'swipe' ? (
        <defs>
          <clipPath id="proposal-swipe">
            <rect x={swipeX} y={0} width={Math.max(0, VIEW_W - swipeX)} height={VIEW_H} />
          </clipPath>
        </defs>
      ) : null}

      <g clipPath={clip}>
        {body.regions.map((r) =>
          open.has(r.id) ? (
            <RegionShape
              key={r.id}
              id={`proposed:${r.id}`}
              kind={r.kind}
              label={r.label}
              poly={r.poly}
              colour={kindColour(vocab, r.kind, 'region_kind')}
              confidence={r.provenance.confidence}
              hovered={focus === r.id}
              ghost
            />
          ) : null,
        )}

        {body.paths.map((p) =>
          open.has(p.id) ? (
            <PathShape
              key={p.id}
              id={`proposed:${p.id}`}
              kind={p.kind}
              points={p.points}
              closed={p.closed}
              colour={kindColour(vocab, p.kind, 'path_kind')}
              label={p.label}
              hovered={focus === p.id}
              ghost
            />
          ) : null,
        )}

        {body.anchors.map((a) =>
          open.has(a.id) ? (
            <AnchorMarker
              key={a.id}
              id={`proposed:${a.id}`}
              kind={a.kind}
              label={a.label}
              at={a.at}
              stand={a.stand}
              facing={a.facing}
              colour={kindColour(vocab, a.kind, 'anchor_kind')}
              radius={focus === a.id ? 1.3 : 0.9}
              hovered={focus === a.id}
              ghost
            />
          ) : null,
        )}

        {body.portals.map((p) =>
          open.has(p.id) ? (
            <PortalMarker
              key={p.id}
              id={`proposed:${p.id}`}
              kind={p.kind}
              edge={p.edge}
              at={anchorAt(p.anchor_id)}
              linked={Boolean(p.anchor_id ?? p.to)}
              colour={kindColour(vocab, p.kind, 'portal_kind')}
              radius={1.2}
              hovered={focus === p.id}
              ghost
            />
          ) : null,
        )}

        {(review.diff?.pairs ?? [])
          .filter((p) => p.status === 'changed' && open.has(p.proposed))
          .map((p) => {
            const a = pctToView(p.from)
            const b = pctToView(p.to)
            return (
              <g key={`${p.current}->${p.proposed}`}>
                <line
                  x1={a[0]}
                  y1={a[1]}
                  x2={b[0]}
                  y2={b[1]}
                  stroke="var(--color-accent)"
                  strokeWidth={focus === p.proposed ? 0.4 : 0.25}
                  strokeDasharray="0.9 0.7"
                  vectorEffect="non-scaling-stroke"
                />
                <circle cx={b[0]} cy={b[1]} r={0.5} fill="var(--color-accent)" />
              </g>
            )
          })}
      </g>

      {proposal.view === 'swipe' ? (
        <line x1={swipeX} y1={0} x2={swipeX} y2={VIEW_H} stroke="white" strokeWidth={0.3} vectorEffect="non-scaling-stroke" />
      ) : null}
    </g>
  )
}
